import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Heart,
  Target,
  Eye,
  Shield,
  Users,
  Sparkles,
  ArrowRight,
  Quote,
} from "lucide-react";

const values = [
  {
    icon: Sparkles,
    title: "Empowerment",
    description:
      "Supporting young people to take ownership of their journey and decisions.",
  },
  {
    icon: Shield,
    title: "Protection",
    description:
      "Safeguarding is at the heart of everything we do, ensuring safety and wellbeing.",
  },
  {
    icon: Target,
    title: "Individualised Care",
    description:
      "Tailored support plans that recognise unique strengths and needs.",
  },
  {
    icon: Users,
    title: "Collaboration",
    description:
      "Working together with professionals, families and communities.",
  },
];

export default function AboutPage() {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 bg-gradient-to-br from-slate-50 via-white to-teal-50/30">
        <div className="blob-teal -top-40 -right-40" />
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Heart className="w-4 h-4" />
                About Epic Care
              </div>
              <h1
                className="text-4xl sm:text-5xl font-extrabold text-slate-900 leading-tight mb-6"
                data-testid="about-headline"
              >
                Building brighter futures for{" "}
                <span className="text-teal-600">young people</span>
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed">
                Epic Care was founded with a clear purpose: to provide
                trauma-informed, safeguarding-led support that empowers young
                people to achieve stability, independence and positive futures.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Epic Care */}
      <section className="section-padding bg-white" data-testid="why-epic-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Why Epic Care Was Created
              </h2>
              <div className="space-y-6 text-slate-600">
                <p className="leading-relaxed">
                  We recognised that vulnerable young people often face
                  fragmented support systems that fail to address their complex
                  needs holistically. Too many young people experience placement
                  breakdowns, inconsistent care, and barriers to building
                  meaningful futures.
                </p>
                <p className="leading-relaxed">
                  Epic Care was established to change this. We bring together
                  experienced professionals who understand the importance of
                  consistent, relationship-based support that puts young
                  people's voices at the centre.
                </p>
                <p className="leading-relaxed">
                  Our approach combines structured accommodation with flexible,
                  responsive care that adapts to each young person's evolving
                  needs while maintaining the boundaries and stability they need
                  to thrive.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1573165706511-3ffde6ef1fe3?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                alt="Black women professionals in a meeting discussion"
                className="rounded-3xl shadow-lg w-full h-[450px] object-cover"
                data-testid="team-image"
              />
              <div className="absolute -bottom-6 -right-6 bg-teal-500 text-white rounded-2xl p-6 shadow-xl max-w-xs hidden md:block">
                <Quote className="w-8 h-8 mb-3 opacity-50" />
                <p className="font-medium">
                  "Empowerment and Protection are central to everything we do."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding bg-slate-50" data-testid="mission-vision-section">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Mission */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl p-8 md:p-10 border border-slate-100 shadow-sm"
            >
              <div className="w-14 h-14 bg-slate-900 rounded-2xl flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                Our Mission
              </h3>
              <p className="text-slate-600 leading-relaxed">
                To provide safe, trauma-informed supported accommodation and
                youth support services that empower young people aged 16-25 to
                develop the skills, confidence and stability they need to build
                positive, independent futures.
              </p>
            </motion.div>

            {/* Vision */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-3xl p-8 md:p-10 border border-slate-100 shadow-sm"
            >
              <div className="w-14 h-14 bg-teal-500 rounded-2xl flex items-center justify-center mb-6">
                <Eye className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                Our Vision
              </h3>
              <p className="text-slate-600 leading-relaxed">
                A society where every young person, regardless of their
                background or circumstances, has access to the support they need
                to thrive. We envision communities where vulnerable young people
                are empowered to reach their full potential.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-white" data-testid="values-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Our Values
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              The principles that guide our approach to supporting young people
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <value.icon className="w-8 h-8 text-slate-700" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">
                  {value.title}
                </h3>
                <p className="text-slate-600 text-sm">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership & Oversight */}
      <section className="section-padding bg-slate-50" data-testid="leadership-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              Leadership & Oversight
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Epic Care operates with clear leadership, accountability and safeguarding oversight.
              Our leadership structure supports consistent practice, quality monitoring and safe
              decision-making across the service. We aim to provide a stable environment for young
              people and reassurance for referring professionals.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Charmaine Profile */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm"
              data-testid="profile-charmaine"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-slate-900 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl font-bold text-teal-400">C</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900">
                    Charmaine Laville
                  </h3>
                  <p className="text-teal-600 font-medium">
                    Nominated Individual & Founder
                  </p>
                </div>
              </div>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  Charmaine is the Nominated Individual for Epic Care and provides governance
                  and safeguarding oversight for the service. She has a professional background
                  in social care and service development and has worked with vulnerable young
                  people for several years.
                </p>
                <p>
                  Her responsibilities include monitoring the quality of care, overseeing
                  policies and procedures, reviewing safeguarding practice, and maintaining
                  professional partnerships with Local Authorities and external agencies.
                  Charmaine ensures the service remains accountable, structured and responsive
                  to the needs of young people.
                </p>
              </div>
            </motion.div>

            {/* Valerie Profile */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm"
              data-testid="profile-valerie"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-slate-900 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl font-bold text-teal-400">V</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900">
                    Valerie Laville
                  </h3>
                  <p className="text-teal-600 font-medium">
                    Registered Manager
                  </p>
                </div>
              </div>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  Valerie is the Registered Manager and oversees the day-to-day running of
                  the service, including staff supervision, safeguarding practice, placement
                  suitability and support planning.
                </p>
                <p>
                  She brings over 20 years of experience working in secondary schools and
                  Pupil Referral Units (PRU). Through this work she has supported young
                  people who experienced exclusion, disengagement from education and
                  behavioural difficulties.
                </p>
                <p>
                  Her background provides strong understanding of adolescent development,
                  behaviour support and re-engagement in education, training and routines.
                  Valerie works closely with staff and professionals to promote stability,
                  structure and positive engagement for young people.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-teal-600 to-teal-700" data-testid="about-cta-section">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to work with us?
          </h2>
          <p className="text-teal-100 text-lg mb-8 max-w-xl mx-auto">
            Whether you're looking to make a referral or explore partnership
            opportunities, we're here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/referrals">
              <Button
                size="lg"
                className="bg-white text-teal-700 hover:bg-slate-100 rounded-full px-8"
                data-testid="about-referral-btn"
              >
                Make a Referral
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white/10 rounded-full px-8"
                data-testid="about-contact-btn"
              >
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
